/**
 * AccountSetupFlow.jsx — creates the signed-in person's profile after auth.
 * Shaped by: signup prompt 41; decision D-41-05 (MKT-26).
 * Invariants: the client writes role patient only. A privileged role is written
 *   by provisionInvitedUserProfile, and only for a valid unused invite.
 *   No organization is created here.
 * Related: functions/src/organizations/provisionInvitedUserProfile.js,
 *   firebase/firestore.rules users create.
 */
import React, { useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import firebaseApp, { db } from "services/firebase";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress, Typography, TextField, Alert } from "@mui/material";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { LoadingButton } from "@mui/lab";
import {
  ChatRounded,
  LocalHospitalRounded,
  MedicalInformationRounded,
} from "@mui/icons-material";
import EmergencySearch from "components/EmergencySearch";
import AddressSearch from "components/AddressSearch";
import { getAddress } from "utils/helpers/getAddress";
import { getStateCode } from "utils/helpers/getStateCode";
import * as geohash from "ngeohash";

const STEPS = {
  ACCOUNT_TYPE: 0,
  FACILITY_TYPE: 1,
  FACILITY_INFO: 2,
};

const STAFF_SELF_SERVE_ROLES = new Set([
  "provider",
  "professional",
  "facility",
  "admin",
]);

const buildBaseUserDoc = ({
  user,
  email,
  role,
  productOrigin = null,
  extra = {},
}) => ({
  uid: user.uid,
  email: email || user.email || null,
  name: "",
  lastName: "",
  role,
  product: productOrigin ?? null,
  registrationDate: firebase.firestore.FieldValue.serverTimestamp(),
  onboarding: false,
  ...extra,
});

const AccountSetupFlow = ({
  prefilledEmail = null,
  onComplete,
  onStepChange,
  backHandlerRef,
  onBackFromFirstStep,
  preselectedRole = null,
  /**
   * Skip the role-chooser screen and auto-create the user profile on mount.
   * Used by the invitation flow (caller already knows the invited role).
   * Ignored when preselectedRole is "facility" since facility setup needs more input.
   */
  autoCreateProfile = false,
  /**
   * True when the user is signing up to accept an org invitation. Admins joining
   * this way attach to the inviter's org (via acceptInvitation), so we must NOT
   * mint a new org for them here.
   */
  isInvitationFlow = false,
  /** Pending organization invite token. Required before a non-patient role is written. */
  inviteToken = null,
  onSignupStart,
  /** Called after signup batch completes or fails (pair with onSignupStart). */
  onSignupEnd,
  /**
   * Product the user signed up through (e.g. "voxpont"), stamped onto the
   * Firestore profile so a Cloud Function can grant the matching custom claim.
   * Null for the default HealthDesk/CareMap sign-up.
   */
  productOrigin = null,
}) => {
  const staffJoinNotice =
    !isInvitationFlow && STAFF_SELF_SERVE_ROLES.has(preselectedRole);
  const shouldAutoCreate =
    autoCreateProfile &&
    Boolean(preselectedRole) &&
    !staffJoinNotice &&
    (isInvitationFlow || preselectedRole === "patient");
  const initialStep = STEPS.ACCOUNT_TYPE;
  const [step, setStepInternal] = useState(initialStep);
  const [loading, setLoading] = useState(shouldAutoCreate);
  const [error, setError] = useState("");
  const [selectedRole, setSelectedRole] = useState(
    staffJoinNotice ? "patient" : preselectedRole || "",
  );

  // Wrapper to notify parent of step changes
  const setStep = (newStep) => {
    setStepInternal(newStep);
    onStepChange?.(newStep);
  };

  // Sync preselectedRole on mount - ensures step is correct even with React quirks
  useEffect(() => {
    if (preselectedRole && !staffJoinNotice && selectedRole !== preselectedRole) {
      setSelectedRole(preselectedRole);
    }
    onStepChange?.(step);
  }, []);

  // Invitation flow: caller already knows the role, so skip the chooser
  // entirely and write the user doc on mount.
  useEffect(() => {
    if (!shouldAutoCreate) return;
    const email = prefilledEmail || firebaseApp.auth().currentUser?.email || "";
    createUserProfile(preselectedRole, email);
    // Run once on mount; createUserProfile is stable enough for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      facilityType: "",
      facilityName: "",
      address: null,
      emergency_location: null,
    },
  });

  const watchFacilityType = watch("facilityType");

  const createUserProfile = async (role, email) => {
    setLoading(true);
    setError("");
    onSignupStart?.();

    try {
      const user = firebaseApp.auth().currentUser;
      if (!user) throw new Error("No authenticated user found.");
      const requested = role === "provider" ? "professional" : role;
      // MKT-26: the browser never writes a privileged role. An invite is applied
      // by the server, which reads the role from the invitation document.
      if (isInvitationFlow && requested && requested !== "patient") {
        if (!inviteToken) {
          throw new Error("This invitation is missing. Ask your administrator to send a new one.");
        }
        const provision = firebaseApp
          .functions()
          .httpsCallable("provisionInvitedUserProfile");
        await provision({ token: inviteToken });
      } else {
        await db
          .collection("users")
          .doc(user.uid)
          .set(
            buildBaseUserDoc({ user, email, role: "patient", productOrigin }),
          );
      }

      await onComplete?.(user);
    } catch (err) {
      console.error("Error creating profile:", err);
      setError(err.message || "Failed to create profile.");
    } finally {
      setLoading(false);
      onSignupEnd?.();
    }
  };

  const handleAccountTypeContinue = async () => {
    if (!selectedRole) {
      setError("Please choose an account type.");
      return;
    }
    if (selectedRole === "facility" || selectedRole === "provider" || selectedRole === "admin") {
      setSelectedRole("patient");
    }
    const email = prefilledEmail || firebaseApp.auth().currentUser?.email || "";
    await createUserProfile(selectedRole, email);
  };

  const handleFacilityInfoSubmit = async (data) => {
    setLoading(true);
    setError("");
    onSignupStart?.();

    try {
      const user = firebaseApp.auth().currentUser;
      if (!user) throw new Error("No authenticated user found.");

      const facilityType = data.facilityType;
      const locationId =
        facilityType === "hospital"
          ? String(data.emergency_location?.id)
          : db.collection("locations").doc().id;

      const batch = db.batch();
      const userRef = db.collection("users").doc(user.uid);
      batch.set(
        userRef,
        buildBaseUserDoc({
          user,
          email: prefilledEmail || user.email,
          role: "patient",
          productOrigin,
          extra: { location: locationId },
        }),
      );

      const locationsRef = db.collection("locations").doc(locationId);
      if (facilityType === "hospital") {
        batch.set(
          locationsRef,
          { users: firebase.firestore.FieldValue.arrayUnion(user.uid) },
          { merge: true },
        );
      } else {
        const addressData = data.address;
        const lookup = (name) =>
          addressData.context.find((a) => a?.id.startsWith(name))?.text;
        const formattedAddress = getAddress(addressData);
        const latitude = addressData.center[1];
        const longitude = addressData.center[0];
        const geopoint = new firebase.firestore.GeoPoint(latitude, longitude);
        const hash = geohash.encode(latitude, longitude);

        batch.set(locationsRef, {
          id: locationId,
          title: data.facilityName,
          lat: latitude,
          lng: longitude,
          address: formattedAddress,
          city: lookup("place"),
          state: getStateCode(addressData),
          zip: lookup("postcode"),
          type: "Clinic",
          users: firebase.firestore.FieldValue.arrayUnion(user.uid),
          status: "pending",
          coordinates: geopoint,
          g: { geohash: hash, geopoint },
        });
      }

      await batch.commit();
      await onComplete?.(user);
    } catch (err) {
      console.error("Error creating facility:", err);
      setError(err.message || "Failed to create facility.");
    } finally {
      setLoading(false);
      onSignupEnd?.();
    }
  };

  const handleBack = () => {
    if (step === STEPS.ACCOUNT_TYPE) {
      onBackFromFirstStep?.();
      return;
    }
    if (step === STEPS.FACILITY_TYPE) {
      if (preselectedRole) {
        onBackFromFirstStep?.();
      } else {
        setStep(STEPS.ACCOUNT_TYPE);
        setSelectedRole("");
        setError("");
      }
      return;
    }
    if (step === STEPS.FACILITY_INFO) {
      setStep(STEPS.FACILITY_TYPE);
      setError("");
    }
  };

  useEffect(() => {
    if (backHandlerRef) backHandlerRef.current = handleBack;
  });

  if (shouldAutoCreate) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 240,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Setting up your account...
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {step === STEPS.ACCOUNT_TYPE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Choose your account type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select how you&apos;ll use HealthDesk.
          </Typography>
          {staffJoinNotice && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="fusionmsi.signup.staffNotice"
              sx={{ mb: 2 }}
            >
              Clinicians and staff join through their institution.
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SelectableChoiceCard
              icon={<ChatRounded fontSize="large" />}
              title="Patient"
              description="Get symptom insights, find care, and manage your health journey."
              onClick={() => {
                setSelectedRole("patient");
                setError("");
              }}
              selected={selectedRole === "patient"}
            />
          </Box>
          <LoadingButton
            variant="contained"
            size="large"
            fullWidth
            loading={loading}
            disabled={!selectedRole}
            onClick={handleAccountTypeContinue}
            data-testid="fusionmsi.signup.continue"
            sx={{ py: 1.5, mt: 3 }}
          >
            Continue
          </LoadingButton>
        </>
      )}

      {step === STEPS.FACILITY_TYPE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            What type of facility?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the type of healthcare facility you&apos;re registering.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SelectableChoiceCard
              orientation="vertical"
              icon={<LocalHospitalRounded sx={{ fontSize: 40 }} />}
              title="Emergency Department"
              description="24-hour emergency care"
              onClick={() => {
                setValue("facilityType", "hospital", { shouldDirty: true });
                setStep(STEPS.FACILITY_INFO);
              }}
            />
            <SelectableChoiceCard
              orientation="vertical"
              icon={<MedicalInformationRounded sx={{ fontSize: 40 }} />}
              title="Clinic"
              description="Urgent/immediate care"
              onClick={() => {
                setValue("facilityType", "urgent-care", { shouldDirty: true });
                setStep(STEPS.FACILITY_INFO);
              }}
            />
          </Box>
        </>
      )}

      {step === STEPS.FACILITY_INFO && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Facility details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {watchFacilityType === "hospital"
              ? "Search for your emergency department."
              : "Enter your facility name and address."}
          </Typography>
          <form onSubmit={handleSubmit(handleFacilityInfoSubmit)}>
            {watchFacilityType === "hospital" ? (
              <EmergencySearch control={control} errors={errors} />
            ) : (
              <>
                <Controller
                  name="facilityName"
                  control={control}
                  rules={{ required: "Facility name is required." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Facility Name"
                      fullWidth
                      variant="outlined"
                      error={!!errors.facilityName}
                      helperText={errors.facilityName?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <AddressSearch control={control} errors={errors} />
              </>
            )}
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              loading={loading}
              sx={{ py: 1.5, mt: 3 }}
            >
              Complete registration
            </LoadingButton>
          </form>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export { STEPS as ACCOUNT_SETUP_STEPS };
export default AccountSetupFlow;
